// GES hesaplama — evdeki elektrikli cihazların tüketimine göre.
// Güncel mesken kademeli tarifesi (EPDK, Nisan 2026, vergiler dahil).

import {
  Refrigerator, Flame, Lightbulb, AirVent, WashingMachine, Utensils,
  CupSoda, Tv, ThermometerSun, Shirt, Monitor,
} from "lucide-react";

// Mesken elektrik birim fiyatı (vergiler dahil, TL/kWh)
const ELECTRICITY_PRICE = 7;
const FEEDIN_RATIO = 0.7; // şebekeye verilen fazla üretimin değer oranı

const SUN_HOURS_PER_YEAR = 1650;   // kWh / kWp / yıl (Akdeniz)
const PANEL_WATT = 600;            // Wp / panel
const COST_PER_KWP = 22000;        // ₺ / kWp (geri dönüş hesabı için, gösterilmez)
const CO2_PER_KWH = 0.478;         // kg CO2 / kWh
const PERFORMANCE_RATIO = 0.82;

// watt: cihaz başına anlık güç (W)
export const APPLIANCES = [
  { id: "buzdolabi", name: "Buzdolabı", icon: Refrigerator, watt: 150, maxWatt: 1200, qty: 1, hours: 24 },
  { id: "aydinlatma", name: "Aydınlatma", icon: Lightbulb, watt: 15, maxWatt: 100, qty: 8, hours: 6 },
  { id: "klima", name: "Klima", icon: AirVent, watt: 1200, maxWatt: 2200, qty: 1, hours: 5 },
  { id: "camasir", name: "Çamaşır Makinesi", icon: WashingMachine, watt: 1000, maxWatt: 1500, qty: 1, hours: 1 },
  { id: "bulasik", name: "Bulaşık Makinesi", icon: Utensils, watt: 1500, maxWatt: 1500, qty: 1, hours: 1 },
  { id: "firin", name: "Fırın", icon: Flame, watt: 2000, maxWatt: 2500, qty: 1, hours: 1 },
  { id: "kettle", name: "Su Isıtıcı (Kettle)", icon: CupSoda, watt: 2000, maxWatt: 2200, qty: 1, hours: 0.3 },
  { id: "tv", name: "Televizyon", icon: Tv, watt: 100, maxWatt: 100, qty: 1, hours: 5 },
  { id: "soba", name: "Elektrikli Soba", icon: ThermometerSun, watt: 2000, maxWatt: 2000, qty: 1, hours: 3 },
  { id: "utu", name: "Ütü", icon: Shirt, watt: 1500, maxWatt: 2200, qty: 1, hours: 0.5 },
  { id: "bilgisayar", name: "Bilgisayar", icon: Monitor, watt: 200, maxWatt: 200, qty: 1, hours: 4 },
];

function monthlyBillFor(kwh) {
  return kwh * ELECTRICITY_PRICE;
}

// items: [{ watt, qty, hours }]
export function calculateFromAppliances(items) {
  const dailyKwh = items.reduce((sum, a) => sum + (a.watt * a.qty * a.hours) / 1000, 0);
  const monthlyKwh = dailyKwh * 30;
  const yearlyKwh = dailyKwh * 365;

  const monthlyBill = monthlyBillFor(monthlyKwh);

  const systemKwp = yearlyKwh > 0 ? yearlyKwh / (SUN_HOURS_PER_YEAR * PERFORMANCE_RATIO) : 0;
  const basePanelCount = Math.ceil((systemKwp * 1000) / PANEL_WATT) || 0;
  const panelCount = Math.round(basePanelCount * 1.5);
  const installedKwp = (panelCount * PANEL_WATT) / 1000;

  const yearlyProduction = installedKwp * SUN_HOURS_PER_YEAR * PERFORMANCE_RATIO;

  // Tasarruf: tüketimi karşılayan üretim tam fiyattan, fazla üretim daha düşük değerden.
  const selfUsed = Math.min(yearlyProduction, yearlyKwh);
  const excess = Math.max(0, yearlyProduction - yearlyKwh);
  const yearlySavings = selfUsed * ELECTRICITY_PRICE + excess * ELECTRICITY_PRICE * FEEDIN_RATIO;
  const monthlySavings = yearlySavings / 12;

  // Geri dönüş (yatırım gösterilmez, sadece süre)
  const investment = installedKwp * COST_PER_KWP;
  const paybackYears = yearlySavings > 0 ? investment / yearlySavings : 0;

  const co2Yearly = (yearlyProduction * CO2_PER_KWH) / 1000; // ton
  const treesEquivalent = Math.round((co2Yearly * 1000) / 21);

  // Gerekli inverter gücü: cihazların max çekim gücü (maxWatt × adet) + %25 pay,
  // ardından standart ticari inverter değerine yukarı yuvarlanır.
  // Aydınlatma toplam yük olarak alınır (adetle çarpılmaz).
  const totalLoadW = items.reduce((sum, a) => {
    const mw = a.maxWatt ?? a.watt;
    return sum + (a.id === "aydinlatma" ? mw : mw * a.qty);
  }, 0);
  const rawInverterKw = (totalLoadW * 1.25) / 1000;
  const INVERTER_SIZES = [3, 3.6, 5, 6.2, 8, 10, 11, 12, 15, 16, 20, 25];
  const inverterKw = INVERTER_SIZES.find((s) => s >= rawInverterKw) ?? INVERTER_SIZES[INVERTER_SIZES.length - 1];

  return {
    dailyKwh: Number(dailyKwh.toFixed(1)),
    monthlyKwh: Math.round(monthlyKwh),
    yearlyKwh: Math.round(yearlyKwh),
    monthlyBill: Math.round(monthlyBill),
    systemKwp: installedKwp,
    panelCount,
    inverterKw: inverterKw,
    yearlyProduction: Math.round(yearlyProduction),
    yearlySavings: Math.round(yearlySavings),
    monthlySavings: Math.round(monthlySavings),
    paybackYears: Number(paybackYears.toFixed(1)),
    co2Yearly: Number(co2Yearly.toFixed(1)),
    co2Lifetime: Number((co2Yearly * 25).toFixed(1)),
    treesEquivalent,
  };
}

export const fmtTL = (n) =>
  new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(n) + " ₺";

export const fmtNum = (n, d = 0) =>
  new Intl.NumberFormat("tr-TR", { maximumFractionDigits: d }).format(n);
